import { useChatMessages } from '@ai-sdk-tools/store';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ADDITIONAL_PROMPTS, PINNED_PROMPTS } from './prompts';
import { restoreCursorPosition } from '../../../../lib/contenteditable-utils';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { useChatInputContext } from '../chat-input/chat-input-context';

interface SuggestedPromptsProps {
  pinnedOnly?: boolean;
  maxRows?: number;
  className?: string;
}

function shuffle<T>(items: readonly T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function focusEditorAtEnd(editor: HTMLTextAreaElement | HTMLDivElement | null, text: string) {
  if (!editor) return;

  if (editor instanceof HTMLTextAreaElement) {
    editor.value = text;
    editor.focus();
    editor.setSelectionRange(text.length, text.length);
    return;
  }

  editor.textContent = text;
  editor.focus();
  restoreCursorPosition(editor, text.length);
}

export function SuggestedPrompts({ pinnedOnly, maxRows, className }: SuggestedPromptsProps) {
  const isFlagEnabled = useFeatureFlag('nova-suggested-prompts', false);
  const messages = useChatMessages<UIMessageWithMetadata>();
  const { input, setInput, editorRef } = useChatInputContext();
  const overlayContext = useChatOverlayContextSafe();
  const analytics = useAnalytics();
  const rowRef = useRef<HTMLDivElement>(null);
  const hasTrackedClickRef = useRef(false);

  const [prompts] = useState(() =>
    pinnedOnly ? [...PINNED_PROMPTS] : [...PINNED_PROMPTS, ...shuffle(ADDITIONAL_PROMPTS)],
  );

  const isNewChat = Boolean(overlayContext?.isNewChat);
  const shouldShow = isFlagEnabled && isNewChat && messages.length === 0;

  useEffect(() => {
    if (messages.length === 0) {
      hasTrackedClickRef.current = false;
    }
  }, [messages.length]);

  useLayoutEffect(() => {
    if (!shouldShow || !maxRows) return;
    const container = rowRef.current;
    if (!container) return;

    const trim = () => {
      const children = Array.from(container.children) as HTMLElement[];
      children.forEach((c) => {
        c.style.display = '';
      });
      // Force layout flush before measuring offsetTop
      void container.offsetWidth;

      const seenTops: number[] = [];
      let cutoff = children.length;
      for (let i = 0; i < children.length; i += 1) {
        const top = children[i].offsetTop;
        if (!seenTops.includes(top)) {
          if (seenTops.length >= maxRows) {
            cutoff = i;
            break;
          }
          seenTops.push(top);
        }
      }
      for (let i = cutoff; i < children.length; i += 1) {
        children[i].style.display = 'none';
      }
    };

    trim();
    const observer = new ResizeObserver(trim);
    observer.observe(container);
    return () => observer.disconnect();
  }, [shouldShow, maxRows]);

  if (!shouldShow) return null;

  const handleSelect = (prompt: string, position: number) => {
    if (!hasTrackedClickRef.current) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SUGGESTED_PROMPT_CLICKED, {
        prompt,
        position,
        isPinned: PINNED_PROMPTS.includes(prompt),
        pinnedOnly: Boolean(pinnedOnly),
      });
      hasTrackedClickRef.current = true;
    }
    const trimmed = input.trimEnd();
    const nextValue = trimmed ? `${trimmed} ${prompt}` : prompt;
    setInput(nextValue);
    focusEditorAtEnd(editorRef.current, nextValue);
  };

  return (
    <div
      data-testid="nova-suggested-prompts"
      role="region"
      aria-label="Suggested prompts"
      className={cn('flex-shrink-0 px-2 pb-2', className)}
    >
      <div ref={rowRef} role="list" className="mx-auto flex w-full max-w-3xl flex-wrap gap-2">
        {prompts.map((prompt, index) => (
          <button
            key={prompt}
            type="button"
            role="listitem"
            aria-label={`Suggested prompt: ${prompt}`}
            data-testid={`nova-suggested-prompt-${index}`}
            onClick={() => handleSelect(prompt, index)}
            className="bg-lime-blue-gradient inline-flex rounded-[30px] p-[2px] transition-opacity hover:opacity-80 focus:opacity-80 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500"
          >
            <span className="text-steel-500 inline-flex items-center rounded-[28px] bg-white px-3 py-1 text-sm leading-5">
              {prompt}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
